import React, { useState, useEffect } from "react";

/**
 * Simple image gallery with lightbox-style expansion.
 *
 * Props:
 * - images: Array<{ src: string, alt: string, caption?: string }>
 * - columns: number (default: 2)
 */
const ImageGallery = ({ images = [], columns = 2 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const openLightbox = (index) => {
    setActiveIndex(index);
    setIsOpen(true);
  };

  const closeLightbox = () => {
    setIsOpen(false);
  };

  const showPrev = (e) => {
    e?.stopPropagation();
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const showNext = (e) => {
    e?.stopPropagation();
    setActiveIndex((prev) => (prev + 1) % images.length);
  };

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      } else if (event.key === "ArrowRight") {
        setActiveIndex((prev) => (prev + 1) % images.length);
      } else if (event.key === "ArrowLeft") {
        setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, images.length]);

  if (!images || images.length === 0) return null;

  const columnClass = columns === 3 ? "md:grid-cols-3" : "md:grid-cols-2";

  return (
    <>
      <div className={`grid ${columnClass} gap-6`}>
        {images.map((image, index) => (
          <button
            key={image.src + index}
            type="button"
            onClick={() => openLightbox(index)}
            className="group text-left bg-gray-50 border border-slate-200 rounded-2xl overflow-hidden shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <div className="relative overflow-hidden">
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            {/* Caption intentionally omitted here so descriptions only appear in the lightbox */}
          </button>
        ))}
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
          onClick={closeLightbox}
        >
          <div
            className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-stretch"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={closeLightbox}
              className="absolute top-3 right-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              aria-label="Close image"
            >
              ✕
            </button>

            {/* Image */}
            <div className="flex-1 flex items-center justify-center bg-slate-900/60 rounded-2xl overflow-hidden border border-slate-700">
              <img
                src={images[activeIndex].src}
                alt={images[activeIndex].alt}
                className="max-h-[80vh] w-auto object-contain"
              />
            </div>

            {/* Caption + controls */}
            <div className="mt-3 flex items-center justify-between text-xs md:text-sm text-slate-100">
              <div className="flex-1 pr-4">
                {images[activeIndex].caption && (
                  <p className="line-clamp-2 md:line-clamp-none">
                    {images[activeIndex].caption}
                  </p>
                )}
              </div>
              {images.length > 1 && (
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={showPrev}
                    className="px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-xs md:text-sm"
                  >
                    Prev
                  </button>
                  <span className="text-[11px] md:text-xs opacity-70">
                    {activeIndex + 1} / {images.length}
                  </span>
                  <button
                    type="button"
                    onClick={showNext}
                    className="px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-xs md:text-sm"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageGallery;
